// src/components/pdf-viewer/VoiceSearchButton.tsx
//
// 🎤 mic for search inputs (used as an antd Input `suffix`). Uses the browser's
// built-in speech recognition (Chrome/Edge/Safari) — free, no key; the browser
// does the transcription. Renders nothing where unsupported (e.g. Firefox).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, message } from 'antd';
import { AudioOutlined } from '@ant-design/icons';

const SpeechRecognitionCtor: any =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Microphone access is blocked. Allow it in your browser to search by voice.',
  'service-not-allowed': 'Voice search is turned off in this browser.',
  'audio-capture': 'No microphone was found. Check your microphone (and your computer’s privacy settings).',
  'no-speech': 'Didn’t catch that. Tap the mic and try again.',
  network: 'Voice search couldn’t reach the speech service. Check your connection, or try Chrome or Safari.',
};

interface VoiceSearchButtonProps {
  /** Called with the spoken text (trimmed, trailing punctuation removed). */
  onResult: (text: string) => void;
  /** Icon colour when idle (e.g. white on the dark narration bar). */
  color?: string;
}

export const VoiceSearchButton: React.FC<VoiceSearchButtonProps> = ({ onResult, color }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  // Keep the latest callback without restarting recognition when it changes.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => () => recognitionRef.current?.abort(), []);

  const toggle = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e: any) => {
      // Strip trailing punctuation some browsers add ("Water heater.")
      const text = (e.results?.[0]?.[0]?.transcript || '').trim().replace(/[.?!,]+$/, '');
      if (text) onResultRef.current(text);
    };
    recognition.onerror = (e: any) => {
      const err = e?.error;
      console.warn('[VoiceSearchButton] voice search error:', err, e?.message);
      if (err === 'aborted') return; // we stopped it ourselves
      message.warning(ERROR_MESSAGES[err] || `Voice search couldn’t start (${err || 'unknown error'}).`);
    };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  }, [listening]);

  if (!SpeechRecognitionCtor) return null;

  return (
    <Tooltip title={listening ? 'Listening… tap to stop' : 'Search by voice'}>
      <AudioOutlined
        role="button"
        aria-label={listening ? 'Stop voice search' : 'Search by voice'}
        onClick={toggle}
        style={{ color: listening ? '#EF4444' : color, opacity: listening ? 1 : 0.7, cursor: 'pointer' }}
      />
    </Tooltip>
  );
};
